import React from "react";

import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";

import type { ICellRendererParams } from "@/ui/molecules/Grid";

import type { ExtractionsErrorListProps } from "@/ui/organisms/ExtractionsSidebar/ExtractionsErrorList";

import type {
    TAllRowsAvailable,
    TRowMappingsWithOperatingStatements,
    TRowMappings,
    TRowReviewWithOperatingStatements,
} from "../typesGrids";
import { isRowTotal } from "../typeChecksGrids";
import { formatNumber, getPinnedRowBackgroundColor } from "../utils";
import { editedFieldKey } from "@/store/tableExtractions/formatUtils";
import { Icon, IconBox } from "@proda-ai/ui-kit";
export const testIds = {
    normal: "GridRendererNumber-normal",
    error: "GridRendererNumber-error",
    greyedOut: "GridRendererNumber-greyedOut",
};

const cellStylesWithEllipsis = {
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    display: "block",
};

type GridReviewRendererNumberProps = Omit<
    ICellRendererParams<TAllRowsAvailable | TRowReviewWithOperatingStatements>,
    "value"
> & {
    errorGroups: ExtractionsErrorListProps["errors"];
    value: number | null;
    isGreyedOut?: boolean;
    isDisabled?: boolean;
    isAltered?: boolean;
    varianceFavorableAbove?: boolean;
    textColor?: string;
};

// @HACK this is a hack to make the section headers visually look like spanned headers
// colSpan is not working for pinned rows we do have in annualization grid
const useSpannedHeaderCellStyleAndMakeItNotClickable = (
    isHeader: boolean,
    ref: React.RefObject<HTMLDivElement>,
) => {
    React.useEffect(() => {
        if (isHeader !== true) return;
        if (!ref.current) return;
        if (!ref.current.parentElement) return;

        ref.current.parentElement.style.borderLeft = "none";
        ref.current.parentElement.style.borderRight = "none";
        ref.current.parentElement.style.pointerEvents = "none";
    }, [isHeader, ref]);
};

const GridReviewRendererNumber: React.FC<
    GridReviewRendererNumberProps
> = params => {
    const {
        value,
        varianceFavorableAbove,
        textColor,
        data,
        errorGroups,
        column,
        isGreyedOut = false,
        isDisabled = false,
    } = params;

    // @FIXME these checks below are only valid for the review grid and annualization grid
    const isHeader =
        (data &&
            (data as TRowReviewWithOperatingStatements).type === "HEADER") ||
        false;
    const isCellATotal =
        (data &&
            (isRowTotal(data as TAllRowsAvailable) ||
                (data as TRowReviewWithOperatingStatements).type ===
                    "SECTION")) ||
        (data as TRowReviewWithOperatingStatements).type === "TYPE";

    const isCellNetTotal =
        (data as TRowReviewWithOperatingStatements).type === "TOTAL";

    // @FIXME these checks below are only valid for mapping grid
    const isNonOperating =
        (data as TRowMappingsWithOperatingStatements).currentSectionType ===
        "non-operating";
    const isOmitted =
        (data as TRowMappingsWithOperatingStatements).mappingType === "omit";
    const shouldUseGreyedOutColor = isNonOperating || isOmitted;
    const colId = column?.getColId();
    const isCellValueEdited =
        colId !== undefined &&
        (data as TRowMappingsWithOperatingStatements | undefined)?.[
            editedFieldKey(colId)
        ] === true;
    // Altered = row sign flipped or value manually edited.
    const isAltered =
        ((data as TRowMappings).flipRowSign ?? false) || isCellValueEdited;
    const formattedValue = formatNumber(value);

    const isErrorCell = Object.keys(errorGroups).some(key => {
        return (
            errorGroups[key]?.some(
                error =>
                    error.rowId === data?.rowId &&
                    error.colId === column?.getColId() &&
                    (error.dismissed === false ||
                        error.dismissed === undefined) &&
                    (error.resolved === false || error.resolved === undefined),
            ) ?? false
        );
    });

    const pinnedRowBackgroundColor = getPinnedRowBackgroundColor(data);

    const mainElementRef = React.useRef<HTMLDivElement>(null);
    useSpannedHeaderCellStyleAndMakeItNotClickable(isHeader, mainElementRef);

    return (
        <Box
            ref={mainElementRef}
            data-testid={
                isErrorCell
                    ? testIds.error
                    : isGreyedOut || isDisabled
                      ? testIds.greyedOut
                      : testIds.normal
            }
            data-colid={params.column?.getColId() ?? ""}
            data-rowid={data?.rowId ?? ""}
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingRight: "16px",
                paddingLeft: "16px",
                ...(isErrorCell
                    ? {
                          backgroundColor: "var(--proda-yellow-20)",
                          borderColor: "var(--proda-yellow)",
                          borderWidth: "2px",
                          borderStyle: "solid",
                      }
                    : pinnedRowBackgroundColor !== null
                      ? {
                            backgroundColor: pinnedRowBackgroundColor,
                        }
                      : isCellNetTotal || isCellATotal
                        ? {
                              backgroundColor: "var(--proda-white-l95)",
                          }
                        : isHeader === true
                          ? {
                                backgroundColor: "var(--proda-white-130)",
                            }
                          : isDisabled === true
                            ? {
                                  backgroundColor: "var(--disabled-bg)",
                              }
                            : isGreyedOut === true
                              ? {
                                    backgroundColor: "var(--proda-white-110)",
                                }
                              : {}),
            }}
        >
            {isAltered === true && (
                <IconBox
                    style={{
                        position: "absolute",
                        top: "-5px",
                        right: "-2px",
                        transform: "rotate(-135deg)",
                    }}
                >
                    <Icon
                        icon="caret-down"
                        size="lg"
                        color={"var(--proda-purple)"}
                    />
                </IconBox>
            )}
            <Typography
                variant="body2"
                sx={{
                    fontWeight: isCellATotal ? 700 : 500,
                    color:
                        isDisabled || shouldUseGreyedOutColor
                            ? "var(--text-color-disabled)"
                            : varianceFavorableAbove !== undefined &&
                                typeof value === "number" &&
                                value !== 0
                              ? value > 0 === varianceFavorableAbove
                                  ? "var(--proda-green-120)"
                                  : "var(--proda-red-120)"
                              : textColor ?? "var(--proda-black)",
                    textDecoration: isDisabled ? "line-through" : "none",
                    ...cellStylesWithEllipsis,
                }}
            >
                {formattedValue}
            </Typography>
        </Box>
    );
};

export default GridReviewRendererNumber;
